import { AccountManagementService } from '@accountManagement/account-management.service';
import { Component, OnInit, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { ControlContainer, NgForm, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditInfoPageMode } from '@core/constants';
import { EmployeePackage, PersonalInfoUpdateRequest } from '@core/models';
import {
  AlertService,
  EmployeeProfileService,
  LoadingService,
  UserContextService,
} from '@core/services';
import { BehaviorSubject, take } from 'rxjs';
import { BtnSize, BtnType } from '@shared/enums';

import * as ProgramCodeConstants from '@core/constants/program-code-constants';

//* Base
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ProfileService } from '@profile/profile.service';
import { PersonalInfoService } from '@core/services/personal-info.service';
import { WizardStep } from 'app/features/wizard/wizard-step.interface';
import {
  forEachFormControl,
  keydownRestrictions,
  Predicates,
  SpValidators,
} from '@shared/utils/input-utils';
import { DropdownOptions } from '@shared/models/dropdown-options.interface';

@Component({
  selector: 'sp-personal-form',
  templateUrl: 'personal.component.html',
  styleUrls: ['personal.component.scss', '../forms.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class PersonalComponent implements OnInit, OnDestroy, WizardStep {
  title = 'Edit Your Info';

  btn = BtnType;
  size = BtnSize;
  isUpdateMode = false;
  imageSrc: SafeResourceUrl;
  mfaPhone: string;
  isSameAsMfa = false;

  private isDirtySubject$ = new BehaviorSubject(false);
  readonly isDirty$ = this.isDirtySubject$.asObservable();

  model: PersonalInfoUpdateRequest = { personalInfo: {} } as PersonalInfoUpdateRequest;

  @Output() saveDisplayStatusOutput = new EventEmitter<boolean>();

  @ViewChild(NgForm) form: NgForm;

  validators: { [key: string]: ValidatorFn[] } = {
    required: [Validators.required],
    firstName: [Validators.required, Validators.maxLength(16)],
    lastName: [Validators.required, Validators.maxLength(25)],
    ssn: [Validators.required, Validators.maxLength(9), Validators.minLength(9)],
    email: [Validators.required, Validators.maxLength(50), Validators.email],
    phone: [Validators.required, SpValidators.phone],
    homePhone: [SpValidators.phone],
    emergencyName: [Validators.maxLength(100)],
    emergencyPhone: [],
  };

  restrictions = {
    digitsOnly: keydownRestrictions(Predicates.isDigitsOnly),
    noSpecialChars: keydownRestrictions(Predicates.not(Predicates.isSpecialChar)),
  };

  maskPatterns = {
    // SSN will be returned in the format ***-**-0000
    // NgMask should not mark that field as invalid
    ssn: {
      // Matches digits and asterisk (*) character
      'x': { pattern: new RegExp(`[0-9*]`), symbol: '*' },

      // Matches digits
      '0': { pattern: new RegExp(`[0-9]`) },
    },
  };

  genderOptions: DropdownOptions[] = [
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private employeeProfileService: EmployeeProfileService,
    protected _sanitizer: DomSanitizer,
    private personalService: PersonalInfoService,
    private alertService: AlertService,
    private accountManangementService: AccountManagementService,
    private loading: LoadingService,
    private profileService: ProfileService,
    userContextService: UserContextService
  ) {
    this.validators.emergencyPhone.push(
      SpValidators.onlyIf(this.emergencyNameFilled, Validators.required),
      SpValidators.onlyIf(this.emergencyNameFilled, SpValidators.phone)
    );
    userContextService.userContext$.pipe(take(1)).subscribe(userContext => {
      this.setImageByProgramCode(userContext.programCode);
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      if (EditInfoPageMode.Update === params.get('editPageMode')) {
        this.isUpdateMode = true;
        this.employeeProfileService.employeeProfile$.pipe(take(1)).subscribe(pkg => {
          this.buildPersonalInfoModel(pkg);
          this.getMfaPhoneNumber();
        });
      }
    });
  }

  onMfaCheck(checked: boolean) {
    const mobilePhone = this.form.controls['mobilePhone'];
    this.model.personalInfo.mobilePhone = checked ? this.extractUsNumber(this.mfaPhone) : '';
    mobilePhone.markAsDirty();
    mobilePhone.updateValueAndValidity();
  }

  onModelUpdate() {
    this.isDirtySubject$.next(this.form.dirty);
  }

  onEmergencyNameChanged() {
    const emPhoneCtrl = this.form.controls['emergencyPhone'];
    emPhoneCtrl.setValue(this.model.personalInfo.emergencyPhone);
    emPhoneCtrl.updateValueAndValidity();
  }

  checkValidity = () => {
    if (this.form.valid) return true;

    forEachFormControl(this.form, ctrl => {
      if (ctrl.valid) return;
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    return false;
  };

  onSubmit = () => {
    if (this.form.dirty && this.form.valid) {
      this.profileService
        .canEdit()
        .pipe(take(1))
        .subscribe(({ canEdit }) => {
          if (canEdit) {
            this.sendData();
          } else {
            this.alertService.showEditingDisabledBanner();
          }
        });
    }
  };

  private emergencyNameFilled = () => this.model.personalInfo.emergencyName?.length > 0;

  private getMfaPhoneNumber(): void {
    this.loading.show();
    this.accountManangementService
      .getMfaPhoneNumber()
      .pipe(take(1))
      .subscribe(phone => {
        this.mfaPhone = phone;
        this.loading.hide();
      });
  }

  private buildPersonalInfoModel(pkg: EmployeePackage): void {
    this.model.personalInfo = pkg?.personal ? { ...pkg.personal } : this.model.personalInfo;
  }

  private sendData(): void {
    if (this.model.personalInfo.employeeId && this.model.personalInfo.bclCode) {
      this.personalService
        .updatePersonalInfo(this.model)
        .pipe(take(1))
        .subscribe({
          next: res => {
            if (res.Success) {
              forEachFormControl(this.form, c => c.markAsPristine());
              this.employeeProfileService.refresh().then(() => {
                this.router
                  .navigate(['profile'])
                  .then(() => this.alertService.success('Your changes were saved.'));
              });
            } else {
              console.error(res.ErrorMessage);
            }
          },
          error: e => {
            this.onModelUpdate();
            console.error(e);
          },
        });
    }
  }

  private setImageByProgramCode(programCode: string): void {
    if (
      ProgramCodeConstants.SurepayrollProgramCodeConstants.indexOf(programCode.toLowerCase()) >=
        0 ||
      ProgramCodeConstants.RunpayrollProgramCodeConstants.indexOf(programCode.toLowerCase()) >= 0
    ) {
      this.imageSrc = this._sanitizer.bypassSecurityTrustResourceUrl(
        `assets/edit-state/sure-personal-form.svg`
      );
    } else if (
      ProgramCodeConstants.PaychexProgramCodeConstants.indexOf(programCode.toLowerCase()) >= 0
    ) {
      this.imageSrc = this._sanitizer.bypassSecurityTrustResourceUrl(
        `assets/edit-state/pxgo-personal-form.svg`
      );
    } else if (
      ProgramCodeConstants.TruistProgramCodeConstants.indexOf(programCode.toLowerCase()) >= 0
    ) {
      this.imageSrc = this._sanitizer.bypassSecurityTrustResourceUrl(
        `assets/edit-state/sunt-personal-form.svg`
      );
    }
  }

  private extractUsNumber(phone: string) {
    return phone?.replace(/[^0-9]/g, '').replace(/^1([0-9]{10})/, '$1');
  }

  ngOnDestroy() {
    this.alertService.clearAllAlerts();
  }
}
